// Card quick add: picking a size writes its variant id into the product form.
// Sits inside <s-product-form>, so it can veto a submit (no size yet) before the cart request runs.
// Back/forward navigation can restore radios and the hidden id out of sync (browser form restore
// or back-forward cache), so the hidden id is re-derived from the checked radio on connect and
// on every pageshow. A pick cancelled by an outside script is re-applied (see click listener).
class CardQuickAdd extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#sync();

    window.addEventListener('pageshow', () => this.#sync(), { signal });

    // A script outside the theme (e.g. an app added through the page header) can cancel the click
    // further up the page. The browser then reverts the radio to unchecked once the click finishes.
    // Re-apply the shopper's pick after the click settles, and log it so the source can be traced.
    this.addEventListener(
      'click',
      (event) => {
        const radio = event.target;
        if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio' || radio.disabled) return;
        setTimeout(() => {
          if (!radio.isConnected || radio.checked) return;
          console.warn('[s-card-quick-add] A size click was cancelled by another script; re-selecting it.', { cancelled: event.defaultPrevented, radio });
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }, 0);
      },
      { signal },
    );

    this.addEventListener(
      'change',
      (event) => {
        const radio = event.target;
        if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;
        this.#sync();
        this.#clearError();
      },
      { signal },
    );

    this.addEventListener(
      'submit',
      (event) => {
        this.#sync();
        const idInput = this.querySelector('[data-card-variant-id]');
        if (!(idInput instanceof HTMLInputElement) || idInput.value) return;
        // No size chosen yet: keep the shopper here, prompt, and point them at the sizes.
        event.preventDefault();
        event.stopPropagation();
        const button = this.querySelector('[data-card-add]');
        const error = this.querySelector('[data-add-to-cart-error]');
        if (error instanceof HTMLElement && button instanceof HTMLElement) {
          error.textContent = button.dataset.sizeMessage || '';
          error.hidden = false;
        }
        this.querySelector('input[type="radio"]:not(:disabled)')?.focus();
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  // The checked radio is the single source of truth for the hidden variant id.
  #sync() {
    const idInput = this.querySelector('[data-card-variant-id]');
    if (!(idInput instanceof HTMLInputElement)) return;
    const checked = this.querySelector('input[type="radio"]:checked:not(:disabled)');
    idInput.value = checked instanceof HTMLInputElement ? checked.value : '';
  }

  #clearError() {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }
  }
}

if (!customElements.get('s-card-quick-add')) {
  customElements.define('s-card-quick-add', CardQuickAdd);
}
